import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { camelize } from "@ember/string";
import { isEmpty } from "@ember/utils";
import { classNames } from "@ember-decorators/component";
import { escapeExpression } from "discourse/lib/utilities";
import {
  CREATE_SHARED_DRAFT,
  CREATE_TOPIC,
  EDIT,
  PRIVATE_MESSAGE,
  REPLY,
} from "discourse/models/composer";
import Draft from "discourse/models/draft";
import DropdownSelectBoxComponent from "discourse/select-kit/components/dropdown-select-box";
import { i18n } from "discourse-i18n";
import { pluginApiIdentifiers, selectKitOptions } from "./select-kit";

// Component can get destroyed and lose state
let _topicSnapshot = null;
let _postSnapshot = null;
let _actionSnapshot = null;

export function _clearSnapshots() {
  _topicSnapshot = null;
  _postSnapshot = null;
  _actionSnapshot = null;
}

let _registeredActions = [];
let _warnedConditionIds = new Set();

export function registerComposerAction(opts) {
  _registeredActions.push(opts);
}

export function _clearRegisteredActions() {
  _registeredActions = [];
  _warnedConditionIds = new Set();
}

@classNames("composer-actions")
@pluginApiIdentifiers(["composer-actions"])
@selectKitOptions({
  icon: "iconForComposerAction",
  filterable: false,
  showFullTitle: false,
  preventHeaderFocus: true,
  customStyle: true,
  btnCustomClasses: "composer-actions-btn",
})
export default class ComposerActions extends DropdownSelectBoxComponent {
  @service dialog;
  @service composer;

  seq = 0;

  @computed("action")
  get isEditing() {
    return this.action === EDIT;
  }

  @computed("topic.slow_mode_seconds")
  get isInSlowMode() {
    return this.topic?.slow_mode_seconds > 0;
  }

  @computed("isEditing", "action", "whisper", "noBump", "isInSlowMode")
  get iconForComposerAction() {
    if (this.action === CREATE_TOPIC) {
      return "plus";
    } else if (this.action === PRIVATE_MESSAGE) {
      return "envelope";
    } else if (this.action === CREATE_SHARED_DRAFT) {
      return "far-clipboard";
    } else if (this.whisper) {
      return "far-eye-slash";
    } else if (this.noBump) {
      return "anchor";
    } else if (this.isInSlowMode) {
      return "hourglass-start";
    } else if (this.isEditing) {
      return "pencil";
    } else {
      return "reply";
    }
  }

  contentChanged() {
    this.set("seq", this.seq + 1);
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    let changeContent = false;

    // if we change topic we want to change both snapshots
    if (
      this.topic &&
      (!_topicSnapshot || this.topic.id !== _topicSnapshot.id)
    ) {
      _topicSnapshot = this.topic;
      _postSnapshot = this.post;
      changeContent = true;
    }

    // if we hit reply on a different post we want to change postSnapshot
    if (this.post && (!_postSnapshot || this.post.id !== _postSnapshot.id)) {
      _postSnapshot = this.post;
      changeContent = true;
    }

    if (this.action !== _actionSnapshot) {
      _actionSnapshot = this.action;
      changeContent = true;
    }

    if (changeContent) {
      this.contentChanged();
    }

    this.set("selectKit.isHidden", isEmpty(this.content));
  }

  modifySelection() {
    return {};
  }

  @computed("seq", "composerModel.categoryId", "composerModel.category")
  get content() {
    let items = [];

    if (
      this.action === REPLY &&
      this.topic &&
      this.topic.isPrivateMessage &&
      this.topic.details &&
      (this.topic.details.allowed_users.length > 1 ||
        this.topic.details.allowed_groups.length > 0) &&
      !this.isEditing &&
      _topicSnapshot
    ) {
      items.push({
        name: i18n(
          "composer.composer_actions.reply_as_new_group_message.label"
        ),
        description: i18n(
          "composer.composer_actions.reply_as_new_group_message.desc"
        ),
        icon: "plus",
        id: "reply_as_new_group_message",
      });
    }

    if (
      this.action === REPLY &&
      this.action !== CREATE_TOPIC &&
      this.action !== CREATE_SHARED_DRAFT &&
      this.topic &&
      !this.topic.isPrivateMessage &&
      !this.isEditing &&
      this.currentUser.can_create_topic &&
      _topicSnapshot
    ) {
      items.push({
        name: i18n("composer.composer_actions.reply_as_new_topic.label"),
        description: i18n("composer.composer_actions.reply_as_new_topic.desc"),
        icon: "plus",
        id: "reply_as_new_topic",
      });
    }

    if (
      (this.action !== REPLY && _postSnapshot) ||
      (this.action === REPLY &&
        _postSnapshot &&
        !(this.replyOptions.userAvatar && this.replyOptions.userLink))
    ) {
      items.push({
        name: i18n("composer.composer_actions.reply_to_post.label", {
          postUsername: _postSnapshot.username,
        }),
        description: i18n("composer.composer_actions.reply_to_post.desc"),
        icon: "reply",
        id: "reply_to_post",
      });
    }

    if (
      !this.isEditing &&
      ((this.action !== REPLY && _topicSnapshot) ||
        (this.action === REPLY &&
          _topicSnapshot &&
          this.replyOptions.userAvatar &&
          this.replyOptions.userLink &&
          this.replyOptions.topicLink))
    ) {
      items.push({
        name: i18n("composer.composer_actions.reply_to_topic.label"),
        description: i18n("composer.composer_actions.reply_to_topic.desc"),
        icon: "reply",
        id: "reply_to_topic",
      });
    }

    // if answered post is a whisper, we can only answer with a whisper so no need for toggle
    if (
      this.canWhisper &&
      (!this.replyOptions.postLink ||
        !_postSnapshot ||
        _postSnapshot.post_type !== this.site.post_types.whisper)
    ) {
      items.push({
        name: i18n("composer.composer_actions.toggle_whisper.label"),
        description: i18n("composer.composer_actions.toggle_whisper.desc"),
        icon: "far-eye-slash",
        id: "toggle_whisper",
      });
    }

    if (this.canUnlistTopic) {
      items.push({
        name: i18n("composer.composer_actions.toggle_unlisted.label"),
        description: i18n("composer.composer_actions.toggle_unlisted.desc"),
        icon: "far-eye-slash",
        id: "toggle_unlisted",
      });
    }

    if (this.action === CREATE_TOPIC) {
      if (this.site.shared_drafts_category_id) {
        // Shared Drafts Choice
        items.push({
          name: i18n("composer.composer_actions.shared_draft.label"),
          description: i18n("composer.composer_actions.shared_draft.desc"),
          icon: "far-clipboard",
          id: "shared_draft",
        });
      }
    }

    const showToggleTopicBump =
      this.get("currentUser.staff") ||
      this.get("currentUser.trust_level") === 4;

    if (this.action === REPLY && showToggleTopicBump) {
      items.push({
        name: i18n("composer.composer_actions.toggle_topic_bump.label"),
        description: i18n("composer.composer_actions.toggle_topic_bump.desc"),
        icon: "anchor",
        id: "toggle_topic_bump",
      });
    }

    if (
      this.currentUser.can_create_topic &&
      this.action !== CREATE_TOPIC &&
      (items.length === 0 || this.action === PRIVATE_MESSAGE)
    ) {
      items.push({
        name: i18n("composer.composer_actions.create_topic.label"),
        description: i18n("composer.composer_actions.create_topic.desc"),
        icon: "share",
        id: "create_topic",
      });
    }

    if (
      this.currentUser.can_send_private_messages &&
      this.action !== PRIVATE_MESSAGE &&
      this.action !== REPLY &&
      !this.isEditing
    ) {
      items.push({
        name: i18n("composer.composer_actions.create_personal_message.label"),
        description: i18n(
          "composer.composer_actions.create_personal_message.desc"
        ),
        icon: "envelope",
        id: "create_private_message",
      });
    }

    _registeredActions.forEach((opts) => {
      let visible = true;
      if (opts.condition) {
        try {
          visible = !!opts.condition(this);
        } catch (e) {
          if (!_warnedConditionIds.has(opts.id)) {
            _warnedConditionIds.add(opts.id);
            // eslint-disable-next-line no-console
            console.error(
              `composer-actions: condition for "${opts.id}" threw`,
              e
            );
          }
          visible = false;
        }
      }
      if (!visible) {
        return;
      }
      items.push({
        id: opts.id,
        name: i18n(opts.label),
        description: opts.description ? i18n(opts.description) : undefined,
        icon: opts.icon,
      });
    });

    return items;
  }

  _continuedFromText(post, topic) {
    let url = post?.url || topic?.url;
    const topicTitle = topic?.title;

    if (!url || !topicTitle) {
      return;
    }

    url = `${location.protocol}//${location.host}${url}`;
    const link = `[${escapeExpression(topicTitle)}](${url})`;
    return i18n("post.continue_discussion", {
      postLink: link,
    });
  }

  async _replyFromExisting(options, post, topic) {
    await this.composer.destroyDraft();
    this.composer.close();
    await this.composer.open({
      ...options,
      prependText: this._continuedFromText(post, topic),
    });
  }

  _openComposer(options) {
    this.composer.closeComposer();
    this.composer.open(options);
  }

  toggleWhisperSelected(options, model) {
    model.toggleProperty("whisper");
  }

  toggleUnlistedSelected(options, model) {
    model.toggleProperty("unlistTopic");
  }

  toggleTopicBumpSelected(options, model) {
    model.toggleProperty("noBump");
  }

  replyAsNewGroupMessageSelected(options) {
    const recipients = [];

    const details = this.topic.details;
    details.allowed_users.forEach((u) => recipients.push(u.username));
    details.allowed_groups.forEach((g) => recipients.push(g.name));

    options.action = PRIVATE_MESSAGE;
    options.recipients = recipients.join(",");
    options.archetypeId = "private_message";

    this._replyFromExisting(options, _postSnapshot, _topicSnapshot);
  }

  replyToTopicSelected(options) {
    options.action = REPLY;
    options.topic = _topicSnapshot;
    this._openComposer(options);
  }

  replyToPostSelected(options) {
    options.action = REPLY;
    options.post = _postSnapshot;
    this._openComposer(options);
  }

  replyAsNewTopicSelected(options) {
    Draft.get("new_topic").then((response) => {
      if (response.draft) {
        this.dialog.confirm({
          message: i18n("composer.composer_actions.reply_as_new_topic.confirm"),
          confirmButtonLabel: "composer.ok_proceed",
          didConfirm: () => this._replyAsNewTopicSelect(options),
        });
      } else {
        this._replyAsNewTopicSelect(options);
      }
    });
  }

  _replyAsNewTopicSelect(options) {
    options.action = CREATE_TOPIC;
    options.draftKey = this.composer.topicDraftKey;
    options.categoryId = this.get("composerModel.topic.category.id");
    options.disableScopedCategory = true;
    this._replyFromExisting(options, _postSnapshot, _topicSnapshot);
  }

  replyAsPrivateMessageSelected(options) {
    let usernames;

    if (_postSnapshot && !_postSnapshot.get("yours")) {
      const postUsername = _postSnapshot.get("username");
      if (postUsername) {
        usernames = postUsername;
      }
    } else if (this.get("composerModel.topic")) {
      const stream = this.get("composerModel.topic.postStream");

      if (stream.get("firstPostPresent")) {
        const post = stream.posts[0];
        if (post && !post.get("yours") && post.get("username")) {
          usernames = post.get("username");
        }
      }
    }

    options.action = PRIVATE_MESSAGE;
    options.recipients = usernames;
    options.archetypeId = "private_message";

    this._replyFromExisting(options, _postSnapshot, _topicSnapshot);
  }

  _switchCreate(options, composerAction) {
    options.action = composerAction;
    options.categoryId = this.get("composerModel.categoryId");
    options.topicTitle = this.get("composerModel.title");
    options.tags = this.get("composerModel.tags");
    this._openComposer(options);
  }

  createTopicSelected(options) {
    this._switchCreate(options, CREATE_TOPIC);
  }

  sharedDraftSelected(options) {
    this._switchCreate(options, CREATE_SHARED_DRAFT);
  }

  createPrivateMessageSelected(options) {
    options.archetypeId = "private_message";
    this._switchCreate(options, PRIVATE_MESSAGE);
  }

  @action
  onChange(value) {
    const registered = _registeredActions.find((a) => a.id === value);
    if (registered) {
      registered.action(this.composerModel, this);
      this.contentChanged();
      return;
    }

    const composerAction = `${camelize(value)}Selected`;
    if (this[composerAction]) {
      this[composerAction](
        this.composerModel.getProperties(
          "draftKey",
          "draftSequence",
          "title",
          "reply",
          "disableScopedCategory"
        ),
        this.composerModel
      );
      this.contentChanged();
    } else {
      // eslint-disable-next-line no-console
      console.error(`No method '${composerAction}' found`);
    }
  }
}
